import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

type UserRole = 'client' | 'lawyer' | 'admin';
type AccountStatus = 'pending' | 'approved';

const PENDING_APPROVAL_MESSAGE = 'Your account is pending approval from the Admin.';

interface AuthContextType {
  user: User | null;
  role: UserRole | null;
  accountStatus: AccountStatus | null;
  assignedLawyerId: string | null;
  assignedClients: string[] | null;
  loading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ 
  user: null, 
  role: null, 
  accountStatus: null, 
  assignedLawyerId: null, 
  assignedClients: null, 
  loading: true,
  logout: async () => {} 
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);
  const [assignedLawyerId, setAssignedLawyerId] = useState<string | null>(null);
  const [assignedClients, setAssignedClients] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [unsubscribeRef, setUnsubscribeRef] = useState<{ current: (() => void) | undefined }>({ current: undefined });

  const logout = async () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = undefined;
    }
    await signOut(auth);
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      
      // Cleanup previous listener
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = undefined;
      }

      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        
        // Use onSnapshot to handle real-time and offline data automatically
        const unsub = onSnapshot(userRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            const normalizedRole = data.role ? data.role.toLowerCase() : null;
            const normalizedStatus = data.status ? data.status.toLowerCase() : 'approved';

            if (normalizedRole === 'lawyer' && normalizedStatus !== 'approved') {
              sessionStorage.setItem('auth_notice', PENDING_APPROVAL_MESSAGE);
              
              if (unsub) unsub();
              unsubscribeRef.current = undefined;

              setUser(null);
              setRole(null);
              setAccountStatus('pending');
              setAssignedLawyerId(null);
              setAssignedClients(null);
              setLoading(false);
              
              signOut(auth).catch((error) => {
                console.error('Error signing out pending lawyer:', error);
              });
              return;
            }

            setRole(normalizedRole as UserRole | null);
            setAccountStatus(normalizedStatus as AccountStatus);
            setAssignedLawyerId(data.assignedLawyerId || null);
            setAssignedClients(data.assignedClients || null);
          } else {
            console.warn("User document does not exist in Firestore.");
            setRole(null);
            setAccountStatus(null);
            setAssignedLawyerId(null);
            setAssignedClients(null);
          }
          setLoading(false);
        }, (error) => {
          // Ignore permission denied errors during logout/auth state changes
          if (error.code !== 'permission-denied') {
            console.error("Error listening to user document:", error);
          }
          setRole(null);
          setAccountStatus(null);
          setAssignedLawyerId(null);
          setAssignedClients(null);
          setLoading(false);
        });
        
        unsubscribeRef.current = unsub;
      } else {
        setRole(null);
        setAccountStatus(null);
        setAssignedLawyerId(null);
        setAssignedClients(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, role, accountStatus, assignedLawyerId, assignedClients, loading, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
